import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Card,
  CardActionArea,
  CardMedia,
  Alert,
  useMediaQuery
} from '@mui/material';
import { styled } from '@mui/system';
import { getCaptcha, verifyCaptcha } from '../../services/apiService';
import CustomLoader from "../Common/CustomLoader"; // Added import for CustomLoader
import CloseIcon from "@mui/icons-material/Close"; // Import CloseIcon

const API_URL = process.env.REACT_APP_API_URL;

const CaptchaContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '40px', // Increased padding
  background: 'linear-gradient(135deg, #ffffff, #d1d9e6)', // Updated gradient
  borderRadius: '12px',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  width: '100%', // Adjusted width
  maxWidth: '1300px', // Increased max width for clarity
  maxHeight: '90vh', // Restored scroll functionality
  overflowY: 'auto', // Restored scroll
  margin: '20px auto',
  textAlign: 'center',
  border: `2px solid #2F4E8B`, // Updated border color
  scrollbarColor: '#2F4E8B #ffffff', // Blue scrollbar
  scrollbarWidth: 'thin', // For Firefox
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#2F4E8B', // Blue thumb for clarity
    borderRadius: '4px',
  },
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    padding: '15px',
  },
}));

const IMAGE_DIMENSION = 150; // Smaller images for better fit

const MainImageWrapper = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'center',
  margin: '20px 0',
}));

const MainImage = styled('img')(() => ({
  width: IMAGE_DIMENSION,
  height: IMAGE_DIMENSION,
  objectFit: 'cover',
  borderRadius: '12px',
  boxShadow: '0 3px 6px rgba(0, 0, 0, 0.15)',
  border: '3px solid #2F4E8B', // Updated border color
  transition: 'transform 0.3s ease-in-out, border 0.3s ease-in-out',
  '&:hover': {
    transform: 'scale(1.05)',
    border: '2px solid #2F4E8B', // Updated hover border color
  },
}));

const OptionCard = styled(Card)(({ isselected }) => ({
  width: IMAGE_DIMENSION,
  height: IMAGE_DIMENSION,
  objectFit: 'cover',
  borderRadius: '12px',
  boxShadow: '0 3px 6px rgba(0, 0, 0, 0.15)',
  border: isselected === 'true' ? '4px solid #ff0000' : '3px solid #2F4E8B', // Updated border color
  transition: 'transform 0.3s ease-in-out, border 0.3s ease-in-out',
  '&:hover': {
    transform: 'scale(1.05)',
    border: '2px solid #2F4E8B', // Updated hover border color
  },
}));

const OptionCardActionArea = styled(CardActionArea)(() => ({
  cursor: 'pointer',
}));

const OptionCardMedia = styled(CardMedia)(() => ({
  width: IMAGE_DIMENSION,
  height: IMAGE_DIMENSION,
  objectFit: 'cover',
  borderRadius: '10px 10px 0 0',
  margin: 'auto',
}));

const Captcha = ({ onSuccess, handleClose }) => {
  const [captcha, setCaptcha] = useState(null);
  const [selected, setSelected] = useState(null);
  const [alert, setAlert] = useState({ type: '', message: '' });

  const isSmallScreen = useMediaQuery('(max-width: 600px)'); // Detect mobile

  useEffect(() => {
    getCaptcha()
      .then((data) => setCaptcha(data))
      .catch((error) =>
        console.error('Erreur lors du chargement du captcha:', error)
      );
  }, []);

  const toggleSelect = (imageId) => {
    setSelected((prev) => (prev === imageId ? null : imageId));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!captcha) return;

    verifyCaptcha({ captchaId: captcha.captchaId, selectedImages: [selected] })
      .then((response) => {
        if (response.success) {
          setAlert({ type: 'success', message: "Vous n'êtes pas un robot!" });
          if (onSuccess) onSuccess({ captchaId: captcha.captchaId, selectedImages: [selected] });
        } else {
          setAlert({
            type: 'error',
            message: 'Mauvaise réponse. Veuillez réessayer.',
          });
          setTimeout(() => {
            window.location.reload();
          }, 2000);
        }
      })
      .catch((error) => {
        console.error('Erreur lors de la vérification du captcha:', error);
        setAlert({
          type: 'error',
          message: 'Erreur lors de la vérification du captcha.',
        });
      });
  };

  const shuffleImages = () => {
    if (captcha) {
      const shuffledOptions = captcha.options.sort(() => Math.random() - 0.5);
      setCaptcha({ ...captcha, options: shuffledOptions });
    }
  };

  if (!captcha) {
    return (
      <CaptchaContainer>
        <CustomLoader />
        <Typography variant="h6" color="textSecondary" sx={{ mt: 2 }}>
          Chargement du captcha...
        </Typography>
      </CaptchaContainer>
    );
  }

  return (
    <CaptchaContainer>
      <Box sx={{ position: "absolute", top: 8, right: 8 }}>
        <CloseIcon
          onClick={handleClose}
          sx={{ color: "#2F4E8B", cursor: "pointer" }} // Close icon
        />
      </Box>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 'medium', color: '#2F4E8B' }}>
        {captcha.question}
      </Typography>
      <MainImageWrapper>
        <MainImage src={`${API_URL}${captcha.leftImage}`} alt="Image Principale" />
      </MainImageWrapper>
      <Typography variant="h6" sx={{ color: '#2F4E8B', marginBottom: '10px' }}>
        Sélectionnez la moitié correspondante
      </Typography>
      {/* FIX: Ensure 4x2 Grid - 4 per row on desktop, 2 per row on mobile */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isSmallScreen ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
          gap: '12px',
          justifyContent: 'center',
          width: '100%',
        }}
      >
        {captcha.options.map((option) => {
          const isSelected = selected === option.id;
          return (
            <div key={option.id} style={{ marginBottom: '12px' }}>
              <OptionCard isselected={isSelected.toString()}>
                <OptionCardActionArea onClick={() => toggleSelect(option.id)}>
                  <OptionCardMedia image={`${API_URL}${option.url}`} alt="Option" />
                </OptionCardActionArea>
              </OptionCard>
            </div>
          );
        })}
      </div>
      {alert.message && (
        <Alert severity={alert.type} sx={{ mb: 2 }}>
          {alert.message}
        </Alert>
      )}
      <Button
        variant="contained"
        onClick={handleSubmit}
        sx={{
          mt: 4,
          width: '100%',
          maxWidth: '250px',
          backgroundColor: '#2F4E8B', // Updated color
          fontWeight: 'bold', // Bold text
          '&:hover': { backgroundColor: '#2F4E8B' }, // Updated hover color
          borderRadius: '6px',
        }}
      >
        Vérifier
      </Button>
      <Button
        variant="outlined"
        onClick={shuffleImages}
        sx={{
          mt: 2,
          width: '100%',
          maxWidth: '250px',
          color: '#2F4E8B', // Updated color
          fontWeight: 'bold', // Bold text
          borderColor: '#2F4E8B', // Updated color
          '&:hover': { borderColor: '#2F4E8B', color: '#2F4E8B' }, // Updated hover color
          borderRadius: '6px',
        }}
      >
        Recharger les images
      </Button>
    </CaptchaContainer>
  );
};

export default Captcha;
